import { Check, ClipboardList, MessageSquare, Phone, Plus, Send, ArrowRight, ArrowUp } from 'lucide-react';
import type { CaseEvent } from '../../api/types';
import Chip from './Chip';
import EvidenceList, { humanizeKey } from './EvidenceList';
import { caseStatusLabel } from '../../lib/caseStatus';
import { timelineTitle } from '../../lib/caseEvents';

const CHANNEL_LABEL: Record<string, string> = {
  phone: 'Phone call', telegram: 'Telegram', email: 'Email', in_person: 'In person',
};

/** Design `.evb` bubble; escalations get the `.evb.esc` accent edge. */
const BUBBLE = 'mt-1.5 rounded-[12px] bg-panel px-3 py-2.5 text-sm text-ink';
const BUBBLE_ESC = 'mt-1.5 rounded-[12px] border-l-[3px] border-accent bg-accent-bg px-3 py-2.5 text-sm text-ink';
const ICON = { size: 18, strokeWidth: 1.8, 'aria-hidden': true } as const;

interface Props {
  event: CaseEvent;
  /** The timeline puts the title in its own header row; standalone use keeps it above the bubble. */
  hideTitle?: boolean;
}

/** Icon tile content and tone for a timeline entry (design `.evi`, `.evi.hot`, `.evi.ok`). */
export function caseEventIcon(kind: string): { icon: JSX.Element; tone: 'default' | 'hot' | 'ok' } {
  switch (kind) {
    case 'note': return { icon: <MessageSquare {...ICON} />, tone: 'default' };
    case 'nudge_sent': return { icon: <Send {...ICON} />, tone: 'default' };
    case 'escalated_to_counselor':
    case 'escalated_to_admin': return { icon: <ArrowUp {...ICON} />, tone: 'hot' };
    case 'status_changed': return { icon: <ArrowRight {...ICON} />, tone: 'default' };
    case 'parent_contacted': return { icon: <Phone {...ICON} />, tone: 'default' };
    case 'outcome_recorded': return { icon: <Check {...ICON} />, tone: 'ok' };
    case 'assignment_created': return { icon: <ClipboardList {...ICON} />, tone: 'ok' };
    case 'case_opened': return { icon: <Plus {...ICON} />, tone: 'default' };
    default: return { icon: <ClipboardList {...ICON} />, tone: 'default' };
  }
}

function FallbackRaw({ event }: { event: CaseEvent }) {
  // An event kind this build does not know yet: readable rows, never raw JSON.
  return (
    <div>
      <p className="text-sm font-medium text-ink">{humanizeKey(event.kind)}</p>
      {event.body && <p className="mt-1 text-sm text-muted">{event.body}</p>}
      <EvidenceList data={(event.payload ?? {}) as Record<string, unknown>} className="mt-1" />
    </div>
  );
}

export default function CaseEventBody({ event, hideTitle = false }: Props) {
  const p = (event.payload ?? {}) as Record<string, any>;
  const title = hideTitle ? null : <p className="text-sm font-semibold text-ink">{timelineTitle(event.kind, p)}</p>;
  const bubble = (text: string, cls = BUBBLE) => (text ? <p className={cls}>{text}</p> : null);

  if (event.kind === 'note') {
    return (
      <div>
        {title}
        <p className={BUBBLE}>{event.body || <span className="italic text-muted">(empty note)</span>}</p>
      </div>
    );
  }

  if (event.kind === 'nudge_sent') {
    return (
      <div>
        {title}
        {bubble(event.body)}
        {p.send_result !== undefined && (
          <div className="mt-2"><Chip tone={p.send_result ? 'up' : 'down'}>{p.send_result ? 'Delivered' : 'Send failed'}</Chip></div>
        )}
      </div>
    );
  }

  if (event.kind === 'escalated_to_counselor' || event.kind === 'escalated_to_admin') {
    return (
      <div>
        {title}
        {bubble(event.body, BUBBLE_ESC)}
      </div>
    );
  }

  if (event.kind === 'status_changed') {
    return (
      <div>
        {title}
        <div className="mt-2 flex flex-wrap items-center gap-1.5">
          <Chip tone="mute">{p.from ? caseStatusLabel(String(p.from)).toLowerCase() : '?'}</Chip>
          <span aria-hidden="true" className="text-muted">→</span>
          <Chip tone="info">{p.to ? caseStatusLabel(String(p.to)).toLowerCase() : '?'}</Chip>
        </div>
      </div>
    );
  }

  if (event.kind === 'parent_contacted') {
    return (
      <div>
        {title}
        {bubble(event.body)}
        {typeof p.channel === 'string' && p.channel && (
          <div className="mt-2"><Chip tone="mute">{CHANNEL_LABEL[p.channel] ?? p.channel}</Chip></div>
        )}
      </div>
    );
  }

  if (event.kind === 'outcome_recorded') {
    const outcome = String(p.outcome ?? '').replace(/_/g, ' ');
    const tone = p.outcome === 'improved' ? 'up' : p.outcome === 'worsened' ? 'down' : 'mute';
    return (
      <div>
        {title}
        {bubble(event.body)}
        <div className="mt-2"><Chip tone={tone}>{outcome || 'unspecified'}</Chip></div>
      </div>
    );
  }

  if (event.kind === 'assignment_created' || event.kind === 'case_opened') {
    return (
      <div>
        {title}
        {bubble(event.body)}
      </div>
    );
  }

  return <FallbackRaw event={event} />;
}
